import React, { ReactElement, forwardRef } from "react"
import { Alert as MUIAlert, AlertProps, AlertTitle } from "@mui/material"
import {
  CheckCircleIcon,
  WarningIcon,
  InfoAlertIcon,
  InfoCircle
} from "../../core/icons"
import { useStyles } from "./styles"

export type AlertSeverity =
  | "success"
  | "warning"
  | "error"
  | "info"
  | "persistent"

export type Props = {
  id?: string
  className?: string
  variant?: "dark" | "light" | "outlined" | "text"
  severity?: AlertSeverity
  size?: "lg" | "md" | "sm"
  icon?: ReactElement | boolean
  title?: React.ReactNode
  action?: ReactElement
  role?: React.AriaRole
  tabIndex?: number
  "aria-labelledby"?: string
  "aria-live"?: AlertProps["aria-live"]
  "data-automation-id"?: string
}

const severityIcons: Record<AlertSeverity, ReactElement | false> = {
  success: <CheckCircleIcon />,
  warning: <WarningIcon />,
  error: <InfoAlertIcon />,
  info: <InfoCircle />,
  persistent: false
}

/*
### MasteryPrep Alert Component

### Props

- `size: 'lg' | 'md' | 'sm'` - sizing styles from Masteryprep styleGuide
<br/>
- `variant: 'dark' | 'light' | 'outline' | 'textOnly'` - variants of alert styling from Masteryprep styleGuide
<br/>
- `severity: 'success' | 'warning' | 'error' | 'info'` - severity default prop from MUI Alert
<br/>
- `className: string` - styling classes to inject into Alert
<br/>
- `icon: ReactElement` - MUI attribute for icon before Alert message 
<br/>
- `title: string` - title default prop from MUI Alert
<br/>
- `action: ReactElement` - action default prop from MUI Alert
*/

const Alert: React.FC<Props> = forwardRef<HTMLDivElement, Props>(
  (
    {
      id,
      size = "md",
      variant = "light",
      severity = "info",
      className,
      icon,
      title,
      action,
      children,
      role,
      tabIndex,
      "aria-labelledby": ariaLabelledBy,
      "aria-live": ariaLive,
      "data-automation-id": dataAutomationId
    },
    ref
  ) => {
    const { classes, cx } = useStyles()

    return (
      <MUIAlert
        ref={ref}
        id={id}
        className={cx(
          classes.base,
          classes[`${variant}_${severity}`],
          variant === "text" && classes[variant],
          classes[size],
          className
        )}
        icon={icon === false || !!icon ? icon : severityIcons[severity]}
        action={action}
        role={role}
        tabIndex={tabIndex}
        aria-labelledby={ariaLabelledBy}
        aria-live={ariaLive}
        data-automation-id={dataAutomationId}
      >
        {title && <AlertTitle>{title}</AlertTitle>}
        {children}
      </MUIAlert>
    )
  }
)

export default Alert
